/**
 * Sentences the server wrote, printed exactly as they arrived.
 *
 * Several payloads in this API carry a `lines` array: the fetch block, the
 * preview of a request for a file, a consent change, a scheduled deletion and
 * a receipt. They are the console's own sentences, written on the side that
 * knows what was and was not checked, and the contract is that a client
 * renders them **verbatim, in order, unabridged, and never beside arithmetic
 * of its own** (`docs/API.md`).
 *
 * This component is where that rule lives, so no screen has to remember it.
 * There is deliberately nothing here that reformats, joins, truncates,
 * highlights or reorders — every one of those is a way of saying something the
 * server did not say.
 */

interface ServerLinesProps {
  lines: readonly string[];
  /** Extra class for the list, e.g. `receipt__lines`. */
  className?: string;
}

/**
 * Render server-authored sentences.
 *
 * Args:
 *     lines: The sentences, in the order the server sent them.
 *     className: Optional extra class on the list.
 *
 * Returns:
 *     One paragraph per line, in order.
 */
export function ServerLines({
  lines,
  className,
}: ServerLinesProps): JSX.Element {
  return (
    <div className={className ? `lines ${className}` : "lines"}>
      {lines.map((line, index) => (
        <p className="lines__line" key={`${index}-${line}`}>
          {line}
        </p>
      ))}
    </div>
  );
}
